
import { useEffect, useMemo, useState, useContext } from 'react';
import { ChevronUp, ChevronDown, Clock, Download, Eye } from 'react-feather';
import { useHistory } from 'react-router';
import { useRemote, minutesIntoReadableFormat } from '../../../Utils/Utils';
import { PAGES, ROUTE_PATHS, BannerContext } from '../../../InternalApiApp';
import { HeaderTickerDisplay, analyticsExportFilenameDateless } from '../AnalyticsUtils';
import Page, { PageContentBodyGrid } from '../../Shared/Page/Page';
import PageGridDivider from '../../Shared/Page/PageGridDivider'
import SearchTermFilter from '../../../components/Input/SearchTermFilter';
import PageGridItem from '../../Shared/Page/PageGridItem';
import Table from '../../../components/Table/Table';
import TickerSectorItem from '../../../components/TickerSectorItem';
import FlexWrapper from '../../FlexWrapper'
import LoadingIcon from '../../Shared/LoadingIcon'
import Tooltip from '../../../components/popups/Tooltip';
import AnalyticsSourceSelector from "../AnalyticsSourceSelector";
import AnalyticsExportButton from '../Components/AnalyticsExportButton';


const AnalyticsPracticesList = () => {
    const { setBannerBreadCrumbs, setBannerAdditionalContent, setBacNoDivider, bannerAdditionalInfo } = useContext(BannerContext);
    const remote = useRemote();
    const history = useHistory();
    const [hasSearchTerm, setHasSearchTerm] = useState("")
    const [filteredData, setFilteredData] = useState([])
    const [loading, setLoading] = useState(true)
    const [aggregates, setAggregates] = useState([]);
    const [sortingColumn, setSortingColumn] = useState({ column: "Name", ascending: true })

    useEffect(() => {
        setBannerBreadCrumbs({ tab: 'Practices' })
        setBacNoDivider(true)
        setBannerAdditionalContent(<AnalyticsSourceSelector />)
        return () => {
            setBacNoDivider(false)
            setBannerAdditionalContent(null)
        }
    }, [])
    // get portfolios
    useEffect(() => {
        const products = bannerAdditionalInfo?.platforms.map(platform =>
            platform.products.map(p => p.selected ? `product=${p.id}` : null)
                .filter(e => e !== null)
                .join("&")
        )
        remote.get(`/funds/portfolio/grouping/Practice/groups/static-data?key=pm_logo&key=cfs_logo${products ? "&" + products : ""}`).then((response) => {
            const practiceObj = response.reduce((a, c) => ({ ...a, [c.groupId]: c }), {})
            remote.get(`analytics/aggregates/groupings${products ? "?" + products : ""}`)
                .then((fetchedGroupings) => {
                    const allPractices = fetchedGroupings.length > 0 ? fetchedGroupings.find((g) => g.groupingName === "Practice").groups : [];
                    const practicesWithLogos = allPractices.map(practice => {
                        const matchPractice = practiceObj[practice.groupId]
                        if (matchPractice) {
                            const pmLogo = matchPractice.keyValues.find(k => k.key === "pm_logo" && k.value.startsWith("url:"))
                            const cfsLogo = matchPractice.keyValues.find(k => k.key === "cfs_logo" && k.value.startsWith("url:"))
                            const logo = pmLogo && pmLogo.value.startsWith("url:") ? pmLogo.value.replace("url:", "") : null
                            return { ...practice, logo: logo ? logo : cfsLogo && cfsLogo.value.startsWith("url:") ? cfsLogo.value.replace("url:", "") : null }
                        } else return { ...practice, logo: null };
                    })
                    setAggregates(practicesWithLogos)
                    setLoading(false)
                });
        })
    }, [remote, setBannerBreadCrumbs, bannerAdditionalInfo])

    // Clear Sorting
    useEffect(() => {
        setSortingColumn({ column: "Name", ascending: true })
    }, [aggregates, filteredData])

    const toggleSort = (column) => {
        if (column === null) {
            setSortingColumn({ column: "Name", ascending: true })
            return
        }

        const ascending = sortingColumn.column === column ? !sortingColumn.ascending : true
        setSortingColumn({ column: column, ascending: ascending })
    }

    const sortTable = (dataToSort) => {
        if (sortingColumn.column === null) {
            return dataToSort
        }
        if (!dataToSort) {
            return dataToSort
        }

        let tableData = dataToSort
        const ascending = sortingColumn.ascending
        switch (sortingColumn.column) {
            case "Name":
                tableData.sort((a, b) => ascending ? a.groupName?.localeCompare(b.groupName) : b.groupName?.localeCompare(a.groupName))
                break;
            case "Minutes":
                tableData.sort((a, b) => ascending ? a.minutes - b.minutes : b.minutes - a.minutes)
                break;
            case "views":
                tableData.sort((a, b) => ascending ? a.views - b.views : b.views - a.views)
                break;
            case "Downloads":
                tableData.sort((a, b) => ascending ? a.downloads - b.downloads : b.downloads - a.downloads)
                break;
            default:
                break;
        }
        return tableData
    }

    // Metrics
    const mostDownloadedPortafolio = aggregates?.toSorted((a, b) => b.downloads - a.downloads)[0]
    const mostViewedPortafolio = aggregates?.toSorted((a, b) => b.views - a.views)[0]

    const dataDisplaying = hasSearchTerm ? filteredData : aggregates

    // table data
    const colWidths = [/*"100px",*/ "auto", "150px", "150px", "150px"];
    const headers = [
        {
            text: (
                <div className='sorting-header' onClick={() => toggleSort("Name")} >
                    Name
                    {sortingColumn && sortingColumn.column === "Name" && sortingColumn.ascending === true ?
                        <ChevronUp className="colour-text-primary" size={12} /> :
                        <ChevronDown className={sortingColumn && sortingColumn.column === "Name" ? "colour-text-primary" : "colour-text-neutral-dark-30"} size={12} />
                    }
                </div>
            )
        },
        // {
        //     text: (
        //         <div />
        //     )
        // },
        {
            className: "text-align-right",
            text: (
                <Tooltip label="Minutes">
                    <div className='sorting-header' onClick={() => toggleSort("Minutes")}>
                        <Clock className="colour-text-primary" size={16} />
                        {sortingColumn && sortingColumn.column === "Minutes" && sortingColumn.ascending === true ?
                            <ChevronUp className="colour-text-primary" size={12} /> :
                            <ChevronDown className={sortingColumn && sortingColumn.column === "Minutes" ? "colour-text-primary" : "colour-text-neutral-dark-30"} size={12} />
                        }
                    </div>
                </Tooltip>
            ),
        },
        {
            className: "text-align-right",
            text: (
                <Tooltip label="Unique Portal Views">
                    <div className='sorting-header' onClick={() => toggleSort("views")}>
                        <Eye className="colour-text-primary" size={16} />
                        {sortingColumn && sortingColumn.column === "views" && sortingColumn.ascending === true ?
                            <ChevronUp className="colour-text-primary" size={12} /> :
                            <ChevronDown className={sortingColumn && sortingColumn.column === "views" ? "colour-text-primary" : "colour-text-neutral-dark-30"} size={12} />
                        }
                    </div>
                </Tooltip>
            )
        },
        {
            className: "text-align-right",
            text: (
                <Tooltip label="Report Downloads">
                    <div className='sorting-header' onClick={() => toggleSort("Downloads")}>
                        <Download className='colour-text-positive-dark' size={16} />
                        {sortingColumn && sortingColumn.column === "Downloads" && sortingColumn.ascending === true ?
                            <ChevronUp className="colour-text-primary" size={12} /> :
                            <ChevronDown className={sortingColumn && sortingColumn.column === "Downloads" ? "colour-text-primary" : "colour-text-neutral-dark-30"} size={12} />
                        }
                    </div>
                </Tooltip>
            )
        }
    ]

    const dataMap = (data) => data.map((practice, idx) => {
        return (
            <tr key={idx} className="cursor-pointer" onClick={() => history.push(`${ROUTE_PATHS[PAGES.ANALYTICS]}/practices/${practice.groupId}`)}>
                {/* <td>
                    <TickerSectorItem
                        imageClass='manager-logo-image-container'
                        image={practice?.logo}
                    />
                </td> */}
                <td>
                    {practice.groupName}
                </td>
                <td><div className="text-align-right">{minutesIntoReadableFormat(practice?.minutes || 0)}</div></td>
                <td><div className="text-align-right">{practice?.views || 0}</div></td>
                <td><div className="text-align-right">{practice?.downloads || 0}</div></td>
            </tr>
        )
    });

    const sortedDataToDisplay = sortTable(dataDisplaying)
    const dataToDisplay = !!sortedDataToDisplay ? dataMap(sortedDataToDisplay) : null

    const summaryExportHeaders = useMemo(() => [
        { label: 'Metric', key: 'metric' },
        { label: 'Value', key: 'value' },
    ], []);
    const summaryExportData = loading ? [] : [
        { metric: 'Total Practices', value: aggregates.length },
        { metric: 'Most Views', value: mostViewedPortafolio?.groupName || '' },
        { metric: 'Most Downloads', value: mostDownloadedPortafolio?.groupName || '' },
    ];
    const tableExportHeaders = useMemo(() => [
        { label: 'Name', key: 'name' },
        { label: 'Minutes', key: 'minutes' },
        { label: 'Views', key: 'views' },
        { label: 'Downloads', key: 'downloads' },
    ], []);
    const tableExportData = (sortedDataToDisplay || []).map(p => ({
        name: p.groupName,
        minutes: p.minutes || 0,
        views: p.views || 0,
        downloads: p.downloads || 0,
    }));

    return (
        <Page noBanner>
            <PageContentBodyGrid rowGap="xl">
                <PageGridItem colSpan="12">
                    <FlexWrapper fullWidth justify="space-between" style={{ paddingBottom: "1rem" }}>
                        <FlexWrapper gap="xl">
                            <FlexWrapper gap="xs" direction="column" align="flex-start" >
                                <label className='total-metrics-label'>Total Practices</label>
                                {loading ? <LoadingIcon size={32} /> : <h2 className='total-metrics-value'>{aggregates.length}</h2>}
                            </FlexWrapper>
                            <Tooltip disabled={!mostViewedPortafolio || (mostViewedPortafolio && mostViewedPortafolio.views === 0)} label={mostViewedPortafolio && `${mostViewedPortafolio.views} views`}>
                                <FlexWrapper gap="xs" direction="column" align="flex-start" >
                                    <label className='total-metrics-label'>Most Views</label>
                                    {loading ? <LoadingIcon size={32} /> : <HeaderTickerDisplay ticker={mostViewedPortafolio} property="views" manager noLogo/>}
                                </FlexWrapper>
                            </Tooltip>
                            <Tooltip disabled={!mostDownloadedPortafolio || (mostDownloadedPortafolio && mostDownloadedPortafolio.downloads === 0)} label={mostDownloadedPortafolio && `${mostDownloadedPortafolio.downloads} downloads`}>
                                <FlexWrapper gap="xs" direction="column" align="flex-start" >
                                    <label className='total-metrics-label'>Most Downloads</label>
                                    {loading ? <LoadingIcon size={32} /> : <HeaderTickerDisplay ticker={mostDownloadedPortafolio} property="downloads" manager noLogo/>}
                                </FlexWrapper>
                            </Tooltip>
                        </FlexWrapper>
                        <AnalyticsExportButton
                            headers={summaryExportHeaders}
                            data={summaryExportData}
                            filename={analyticsExportFilenameDateless('practices-summary')}
                            loading={loading}
                        />
                    </FlexWrapper>
                    <PageGridDivider />
                    <PageGridItem style={{ paddingTop: "1.5rem" }}>
                        <SearchTermFilter
                            size="small"
                            width="l"
                            placeholder={`Search`}
                            isClearable
                            dataSet={aggregates}
                            setFilteredData={setFilteredData}
                            path={["groupName"]}
                            hasSearchTerm={(boolean) => setHasSearchTerm(boolean)}
                        />
                    </PageGridItem>
                    <FlexWrapper fullWidth justify="space-between" align="center">
                        <div className="table-title">All Practices</div>
                        <AnalyticsExportButton
                            headers={tableExportHeaders}
                            data={tableExportData}
                            filename={analyticsExportFilenameDateless('practices-table')}
                            loading={loading}
                        />
                    </FlexWrapper>
                    {loading ? <LoadingIcon centered size={48} /> :
                        <Table
                            minWidth={900}
                            colWidths={colWidths}
                            headers={headers}
                            dataMap={dataToDisplay}
                            noResults={{ message: "No tickers found" }}
                            tableStyle={{ maxWidth: 1200 }}
                        />
                    }
                </PageGridItem>
            </PageContentBodyGrid>
        </Page>
    );
}

export default AnalyticsPracticesList;